'use client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

// Importaciones del servicio de Equipamiento
import {
  fetchAllEquipments,
  createEquipment,
  updateEquipment,
  deleteEquipment,
  EquipmentAPI,
  EquipmentData,
} from '../Services/services';
import { getHeaderConfig } from '../Users/Users';

// Importaciones de los componentes de UI específicos para Equipamiento
import EquipmentFormModal from './components/EquipmentFormModal'; // Asumo que creaste este archivo
import EquipmentList from './components/EquipmentList'; // Asumo que creaste este archivo

// --- Estado Inicial del Formulario ---
const INITIAL_FORM_DATA: EquipmentData = {
  name: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  status: true,
};

function EquipmentPanel({ servicesManager }) {
  const [equipments, setEquipments] = useState<EquipmentAPI[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<EquipmentAPI | null>(null);
  const [formData, setFormData] = useState<EquipmentData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { show } = useModal();
  const [appConfig] = useAppConfig();
  const { menuOptions } = getHeaderConfig({ servicesManager, t, show, navigate, appConfig });

  // --- Lógica de Carga de Datos ---
  const loadEquipments = async () => {
    try {
      setIsLoading(true);
      const apiEquipments = await fetchAllEquipments();
      setEquipments(apiEquipments);
    } catch (error) {
      toast.error('Error al obtener el equipamiento: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Cargar equipos al montar el componente
    loadEquipments();
  }, []);

  // --- Lógica de Filtro ---
  const filteredEquipments = equipments.filter(
    (eq) =>
      eq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Manejadores CRUD ---

  const handleCreateEquipment = async () => {
    try {
      const response = await createEquipment(formData);
      toast.success(`Equipo ${response.name} creado exitosamente.`);
      await loadEquipments();
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear equipo: ' + (err.message || err));
    }
  };

  const handleUpdateEquipment = async () => {
    if (!editingEquipment) return;
    try {
      const response = await updateEquipment(editingEquipment._id, formData);
      toast.success(`Equipo ${response.name} actualizado.`);
      await loadEquipments();
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar equipo: ' + (err.message || err));
    }
  };

  const handleDeleteEquipment = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este equipo?')) return;
    try {
      await deleteEquipment(id);
      toast.success('Equipo eliminado correctamente.');
      setEquipments((prev) => prev.filter((e) => e._id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar el equipo: ' + (err.message || err));
    }
  };

  const handleEditEquipment = (equipment: EquipmentAPI) => {
    setEditingEquipment(equipment);
    setFormData({
      name: equipment.name,
      manufacturer: equipment.manufacturer || '',
      model: equipment.model || '',
      serial_number: equipment.serial_number || '',
      status: equipment.status,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingEquipment(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingEquipment ? handleUpdateEquipment() : handleCreateEquipment();
  };

  // --- Renderizado del Panel ---
  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Equipamiento</h1>
            <p className="mt-1">Administra los dispositivos (máquinas, escáneres, etc.)</p>
          </div>
          <button
            onClick={() => {
              setEditingEquipment(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Crear Equipo
          </button>
        </div>

        {/* Lista de Equipos */}
        <EquipmentList
          equipments={filteredEquipments}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          handleEditEquipment={handleEditEquipment}
          handleDeleteEquipment={handleDeleteEquipment}
        />
      </div>

      {/* Modal de Formulario */}
      <EquipmentFormModal
        isDialogOpen={isDialogOpen}
        editingEquipment={editingEquipment}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
      />
    </div>
  );
}

EquipmentPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default EquipmentPanel;
